/**
 * PluginSlot — host-side mount point for plugin UI bundles.
 *
 * On first render, fetches `/api/plugins/manifest` (cached process-wide).
 * For each enabled plugin whose `ui_slots` includes the slot id, dynamic-
 * imports the plugin's UI bundle (vite library-mode ES module) and renders
 * the component the plugin exported under that slot id.
 *
 * Plugin bundle contract:
 *   export const slots = { "<slot_id>": ReactComponent, ... };
 *
 * Plugins receive a single `ctx` prop containing:
 *   - `host`: bounded host facade with safe re-exports (V2Player, api, etc)
 *   - any pass-through context the slot was rendered with (`session`, `device`)
 */

import React, { useEffect, useState } from 'react';

interface PluginManifestEntry {
  name: string;
  version: string;
  description?: string | null;
  ui_slots: string[];
  routes_under: string;
  ui_url: string | null;
  enabled: boolean;
  error: string | null;
}

interface PluginManifestResponse {
  plugins: PluginManifestEntry[];
}

interface LoadedBundle {
  slots: Record<string, React.ComponentType<any>>;
}

let manifestPromise: Promise<PluginManifestResponse> | null = null;
const bundleCache: Record<string, Promise<LoadedBundle>> = {};

/**
 * Static plugin registry — used while we transition to true external
 * vite-library bundles served from `/api/plugins/<name>/ui/`. Plugins
 * listed here are bundled with the host frontend; the manifest endpoint
 * still gates whether they render so the operator can disable a plugin
 * server-side without a frontend rebuild.
 */
const STATIC_PLUGIN_BUNDLES: Record<string, () => Promise<LoadedBundle>> = {
  synced_viewer: async () => {
    const mod = await import('../plugins/synced_viewer/SyncedViewerPanel');
    return {
      slots: {
        'session.detail.panels': mod.SyncedViewerPanel,
      },
    };
  },
};

async function loadManifest(): Promise<PluginManifestResponse> {
  if (manifestPromise) return manifestPromise;
  manifestPromise = fetch('/api/plugins/manifest')
    .then((r) => (r.ok ? r.json() : { plugins: [] }))
    .catch(() => ({ plugins: [] }));
  return manifestPromise;
}

async function loadBundle(name: string, url: string | null): Promise<LoadedBundle> {
  const cached = bundleCache[name];
  if (cached) return cached;
  // Prefer the static registry (bundled with host) over the dynamic URL
  // — keeps cold-load latency tight and avoids a CORS surface.
  if (STATIC_PLUGIN_BUNDLES[name]) {
    bundleCache[name] = STATIC_PLUGIN_BUNDLES[name]().catch((err) => {
      console.warn(`[PluginSlot] static bundle failed for ${name}:`, err);
      return { slots: {} };
    });
    return bundleCache[name];
  }
  if (url) {
    bundleCache[name] = (import(/* @vite-ignore */ url) as Promise<LoadedBundle>).catch((err) => {
      console.warn(`[PluginSlot] failed to load bundle for ${name}:`, err);
      return { slots: {} };
    });
    return bundleCache[name];
  }
  return { slots: {} };
}

interface PluginSlotProps {
  id: string;
  // Pass-through props plugins commonly need:
  session?: any;
  device?: any;
  /** Anything else — forwarded to plugin component via ctx */
  [key: string]: any;
}

export const PluginSlot: React.FC<PluginSlotProps> = ({ id, ...passthrough }) => {
  const [entries, setEntries] = useState<
    Array<{ name: string; Component: React.ComponentType<any> }>
  >([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const manifest = await loadManifest();
      const matches = manifest.plugins.filter(
        (p) => p.enabled && p.ui_slots.includes(id) && (p.ui_url || STATIC_PLUGIN_BUNDLES[p.name]),
      );
      const loaded: typeof entries = [];
      for (const m of matches) {
        const bundle = await loadBundle(m.name, m.ui_url);
        const Component = bundle.slots[id];
        if (Component) loaded.push({ name: m.name, Component });
      }
      if (!cancelled) setEntries(loaded);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (entries.length === 0) return null;
  return (
    <>
      {entries.map(({ name, Component }) => (
        <PluginErrorBoundary key={name} pluginName={name}>
          <Component ctx={{ ...passthrough }} />
        </PluginErrorBoundary>
      ))}
    </>
  );
};

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class PluginErrorBoundary extends React.Component<
  React.PropsWithChildren<{ pluginName: string }>,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.warn(`[PluginSlot] plugin "${this.props.pluginName}" crashed:`, error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded border border-status-warning/60 bg-bg-elevated p-2 font-mono text-xs text-status-warning">
          plugin "{this.props.pluginName}" crashed: {this.state.error?.message ?? 'unknown'}
        </div>
      );
    }
    return this.props.children;
  }
}

export default PluginSlot;
